import { formatCurrency } from '@mahawi/eshop-common/dist/src/format-currency';
import { getFlag } from '@mahawi/eshop-common/dist/src/get-flag';
import { getHumanReadableTaxValue } from '@mahawi/eshop-common/dist/src/get-human-readable-tax-value';
import {
  DEFAULT_CURRENCY_TYPE_ISO_CODE,
  DEFAULT_LANGUAGE_TYPE,
  EEventContextAction,
  EEventType,
  type IBasketProduct,
  type ICountry,
  type ICountryCurrencyPrice,
  type ICurrencyPrice,
  type ITranslation,
} from '@mahawi/eshop-common/dist/src/types';
import { type Dispatch } from '@reduxjs/toolkit';
import { Field, Form, Formik, useFormikContext } from 'formik';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useOptimistic,
  useRef,
  useState,
  useTransition,
} from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import EmailVerifyBadge from '../../fragments/email-verify-badge';
import PrivacyNoticeLink from '../../fragments/privacy-notice-link';
import { useEmailVerification } from '../../fragments/use-email-verification';
import { addProduct } from '../../reducers/basket/reducer';
import { type IBasketState } from '../../reducers/basket/types';
import { type ICountryState } from '../../reducers/country/types';
import { type RootState } from '../../reducers/customer';
import { EEmailValidatorCorrelationId } from '../../reducers/email-validator/types';
import { eventSend } from '../../reducers/event/reducer';
import { type IProductState } from '../../reducers/product/types';
import { watchdogRegister } from '../../reducers/product-watchdog/reducer';
import { type IProductWatchdogState } from '../../reducers/product-watchdog/types';
import { type IUserState } from '../../reducers/user/types';
import { GuaranteeNoticeBadge } from '../guarantee-notice';
import AddToGiftRegistry from './add-to-gift-registry';
import Stars from './stars';

function WatchdogEmailField({
  placeholder,
  enabled,
}: {
  placeholder: string;
  enabled: boolean;
}): React.JSX.Element {
  const { values, handleBlur, setFieldValue } = useFormikContext<{
    email: string;
  }>();
  const emailVerification = useEmailVerification({
    correlationId: EEmailValidatorCorrelationId.Watchdog,
    email: values.email.trim(),
    enabled,
  });

  return (
    <>
      <Field
        type="email"
        name="email"
        required
        className="product-watchdog--email"
        placeholder={placeholder}
        onBlur={(e: React.FocusEvent<HTMLInputElement>): void => {
          handleBlur(e);
          emailVerification.onBlur();
        }}
      />
      <EmailVerifyBadge
        status={emailVerification.status}
        suggestedEmail={emailVerification.suggestedEmail}
        onUseSuggestion={(suggestedEmail): void => {
          void setFieldValue('email', suggestedEmail);
        }}
      />
    </>
  );
}

function Offer({
  Product,
  ProductWatchdog,
  Basket,
  User,
  dispatch,
  Country,
}: {
  Product: IProductState;
  ProductWatchdog: IProductWatchdogState;
  Basket: IBasketState;
  User: IUserState;
  dispatch: Dispatch;
  Country: ICountryState;
}): React.JSX.Element | undefined {
  const { t } = useTranslation();
  const [showAddedToCartMessage, setShowAddedToCartMessage] = useState(false);
  const [, startTransition] = useTransition();
  const basketResolveRef = useRef<(() => void) | null>(null);
  const emailValidatorEnabled = useSelector(
    (state: RootState) => state.Config.emailValidator.watchdog,
  );

  // All hooks must be called unconditionally before any early return
  const inBasket: IBasketProduct | undefined = useMemo(
    (): IBasketProduct | undefined =>
      Basket.products.find(
        (p: IBasketProduct): boolean => p.uuid === Product.product?.uuid,
      ),
    [Basket.products, Product.product?.uuid],
  );

  const [optimisticCount, addOptimistic] = useOptimistic(
    inBasket?.count || 0,
    (current: number, delta: number): number => current + delta,
  );

  const currencyPrice: ICurrencyPrice | undefined = useMemo(
    (): ICurrencyPrice | undefined =>
      Product.product?.prices.find(
        (cp: ICurrencyPrice): boolean =>
          cp.isoCode ===
          (User.currencyType?.isoCode || DEFAULT_CURRENCY_TYPE_ISO_CODE),
      ),
    [Product.product?.prices, User.currencyType?.isoCode],
  );

  const priceWithTax: ICountryCurrencyPrice | undefined = useMemo(
    (): ICountryCurrencyPrice | undefined =>
      currencyPrice?.pricesWithTax.find(
        (c: ICountryCurrencyPrice): boolean =>
          c.isoCode === (User.countryType?.isoCode || 'CZE'),
      ),
    [currencyPrice, User.countryType?.isoCode],
  );

  const outOfStockMessage: string = useMemo((): string => {
    if (Product.product?.nextDeliveryDate !== '') {
      return t('product.outOfStockDeliveryDate', {
        nextDeliveryDate: Product.product?.nextDeliveryDate,
      });
    }
    return '';
  }, [Product.product?.nextDeliveryDate, t]);

  const stockMessage: string = useMemo(
    (): string => t('product.onStock', { count: Product.product?.stock }),
    [Product.product?.stock, t],
  );

  const lowStockMessage: string = useMemo(
    (): string => t('product.lowStock', { count: Product.product?.stock }),
    [Product.product?.stock, t],
  );

  const LOW_STOCK_THRESHOLD = 5;

  const country: ICountry | undefined = useMemo(
    (): ICountry | undefined =>
      Country.countries?.find(
        (c: { isoCode: string }): boolean =>
          c.isoCode === (User.countryType?.isoCode || 'CZE'),
      ),
    [Country.countries, User.countryType?.isoCode],
  );

  const countryCZE: ICountry | undefined = useMemo(
    (): ICountry | undefined =>
      Country.countries?.find(
        (c: { isoCode: string }): boolean => c.isoCode === 'CZE',
      ),
    [Country.countries],
  );

  const vatRate: string = useMemo(
    (): string =>
      getHumanReadableTaxValue(country?.vatRate || countryCZE?.vatRate || 1.21),
    [country?.vatRate, countryCZE?.vatRate],
  );

  const flag: string = useMemo(
    (): string =>
      getFlag({ countryTypeIsoCode: User.countryType?.isoCode || 'CZE' }),
    [User.countryType?.isoCode],
  );

  // Resolve the optimistic transition once server confirms the basket update
  useEffect((): void => {
    if (!Basket.inProgress && basketResolveRef.current) {
      basketResolveRef.current();
      basketResolveRef.current = null;
    }
  }, [Basket.inProgress]);

  const handleSubmit = useCallback(
    ({ count }: { count: number }): void => {
      if (!Product.product) return;
      const currentInBasket: number = inBasket?.count || 0;
      const remainingStock: number = Product.product.stock - currentInBasket;
      const properCount: number = Math.min(count, remainingStock);

      if (properCount <= 0) return;

      // Optimistically update basket count while waiting for server confirmation
      startTransition(async (): Promise<void> => {
        addOptimistic(properCount);
        await new Promise<void>((resolve): void => {
          basketResolveRef.current = resolve;
        });
      });

      dispatch(
        addProduct({
          product: Product.product,
          count: properCount,
          discounts: Basket.discounts.map((discount) => ({
            code: discount.code,
          })),
        }),
      );
      dispatch(
        eventSend({
          type: EEventType.PRODUCT_ADD_TO_BASKET,
          action: EEventContextAction.CHANGE,
          context: {
            product: { uuid: Product.product.uuid },
            count: properCount,
          },
        }),
      );
      setShowAddedToCartMessage(true);
      const productNameEn: ITranslation | undefined =
        Product.product.names.find(
          (n: ITranslation): boolean => n.code === 'EN',
        );
      const productPriceEur: ICurrencyPrice | undefined =
        Product.product.prices.find(
          (c: ICurrencyPrice): boolean => c.isoCode === 'EUR',
        );
      const priceWithTaxEur: ICountryCurrencyPrice | undefined =
        productPriceEur?.pricesWithTax.find(
          (c: ICountryCurrencyPrice): boolean =>
            c.isoCode === (User.countryType?.isoCode || 'CZE'),
        );
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          items: [
            {
              item_id: Product.product.uuid,
              item_name: productNameEn?.value || '---',
              price: priceWithTaxEur?.price || 0,
              quantity: properCount,
              currency: 'EUR',
            },
          ],
        },
      });
    },
    [
      addOptimistic,
      Basket.discounts,
      dispatch,
      inBasket,
      Product.product,
      startTransition,
      User.countryType?.isoCode,
    ],
  );

  const handleWatchdogSubmit = useCallback(
    ({ email }: { email: string }): void => {
      if (!Product.product || !User.languageType) return;
      dispatch(
        watchdogRegister({
          product: { uuid: Product.product.uuid },
          email,
          languageType: User.languageType,
        }),
      );

      dispatch(
        eventSend({
          type: EEventType.WATCHDOG_REGISTER,
          action: EEventContextAction.ADD,
          context: { product: { uuid: Product.product.uuid } },
        }),
      );
    },
    [dispatch, Product.product, User.languageType],
  );

  useEffect((): (() => void) => {
    let timeout: NodeJS.Timeout | undefined;

    if (showAddedToCartMessage) {
      timeout = setTimeout((): void => {
        setShowAddedToCartMessage(false);
      }, 5000);
    }

    return (): void => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [showAddedToCartMessage]);

  if (Product.isLoading || !Product.product) {
    return undefined;
  }

  return (
    <div className="container--3 container--t">
      <div className="product-info">
        {!Product.product.isActive ? (
          <p className="product-info--discontinued">
            {t('product.discontinued')}
          </p>
        ) : Product.product.stock > LOW_STOCK_THRESHOLD ? (
          <p className="product-info--stock">{stockMessage}</p>
        ) : Product.product.stock > 0 ? (
          <p className="product-info--stock--low">{lowStockMessage}</p>
        ) : (
          <>
            <p className="product-info--out-of-stock">
              {outOfStockMessage || t('product.outOfStock')}
            </p>
            {ProductWatchdog.registered === true ? (
              <p className="product-watchdog--registered">
                {t('product.watchdog.registered')}
              </p>
            ) : (
              <>
                {ProductWatchdog.registered === false && (
                  <p className="product-watchdog--error">
                    {t('product.watchdog.error')}
                  </p>
                )}
                <Formik
                  initialValues={{ email: '' }}
                  onSubmit={handleWatchdogSubmit}
                >
                  <Form className="product-watchdog">
                    <WatchdogEmailField
                      placeholder={t('product.watchdog.email')}
                      enabled={emailValidatorEnabled}
                    />
                    <button
                      type="submit"
                      className="product-watchdog--submit"
                      disabled={ProductWatchdog.isLoading}
                    >
                      {t('product.watchdog.submit')}
                    </button>
                  </Form>
                </Formik>
                <PrivacyNoticeLink />
              </>
            )}
          </>
        )}
        {Product.product.preOrderDays > 0 && (
          <p className="warning">
            {t('product.preOrder', {
              preOrderDays: Product.product.preOrderDays,
            })}
          </p>
        )}
        {Product.product.isActive && currencyPrice && (
          <>
            <p className="product-info--price">
              {t('product.price', {
                price: formatCurrency(
                  priceWithTax?.price || 0,
                  User.currencyType,
                ),
              })}
            </p>
            <p className="product-info--price-without-tax">
              {t('product.priceWithoutTax', {
                price: formatCurrency(
                  currencyPrice.priceWithoutTax,
                  User.currencyType,
                ),
              })}
            </p>
            <GuaranteeNoticeBadge />
          </>
        )}
        {Product.product.averageRating != null &&
          Product.product.reviewCount != null && (
            <Stars
              rating={Product.product.averageRating}
              count={Product.product.reviewCount}
            />
          )}
        {Product.product.isActive && (
          <>
            <p className="product-info--meta">
              {t('product.countryVatRate', {
                countryTypeIsoCode: User.countryType?.isoCode || 'CZE',
                flag,
                vatRate,
              })}
            </p>
            <p className="product-info--meta">
              {t('product.weight', {
                weight: Product.product.dimensions.weight,
              })}
            </p>
          </>
        )}
        {Product.product.isActive && (
          <AddToGiftRegistry productUUID={Product.product.uuid} />
        )}
      </div>

      {Product.product.isActive && Product.product.stock > 0 && (
        <p className="product-info--in-cart">
          {t('product.actualInCart', { count: optimisticCount })}
        </p>
      )}

      {Product.product && Product.product.stock > 0 && (
        <Formik initialValues={{ count: 1 }} onSubmit={handleSubmit}>
          {({ submitForm, values, setFieldValue }): React.JSX.Element => (
            <Form>
              <label htmlFor="count" className="product-count-label">
                {t('product.count')}
              </label>
              <div className="product-count-group">
                <div className="product-count-stepper">
                  <button
                    type="button"
                    className="arrow"
                    onClick={(): void => {
                      void setFieldValue(
                        'count',
                        Math.max(1, values.count - 1),
                      );
                    }}
                    disabled={Basket.inProgress}
                    aria-label="Decrease count"
                    style={{
                      visibility: values.count <= 1 ? 'hidden' : 'visible',
                    }}
                  >
                    <svg
                      className="arrowDownOutlined"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path d="M12 21.312l-7.121-7.121c-1.17-1.17-1.17-3.073 0-4.242 1.094-1.094 2.978-1.138 4.121-.115v-4.834c0-1.654 1.346-3 3-3s3 1.346 3 3v4.834c1.143-1.023 3.027-.979 4.121.115 1.17 1.169 1.17 3.072 0 4.242l-7.121 7.121zm-5-10.242c-.268 0-.518.104-.707.293-.391.39-.391 1.023 0 1.414l5.707 5.707 5.707-5.707c.391-.391.391-1.024 0-1.414-.379-.379-1.035-.379-1.414 0l-3.293 3.293v-9.656c0-.551-.448-1-1-1s-1 .449-1 1v9.656l-3.293-3.293c-.189-.189-.439-.293-.707-.293z" />
                    </svg>
                  </button>
                  <Field
                    id="count"
                    name="count"
                    type="number"
                    disabled={Basket.inProgress}
                    className="product-count"
                    min={1}
                    max={Product.product?.stock}
                    step={1}
                  />
                  <button
                    type="button"
                    className="arrow"
                    onClick={(): void => {
                      void setFieldValue(
                        'count',
                        Math.min(
                          Product.product?.stock || 999,
                          values.count + 1,
                        ),
                      );
                    }}
                    disabled={Basket.inProgress}
                    aria-label="Increase count"
                    style={{
                      visibility:
                        values.count >= (Product.product?.stock || 999)
                          ? 'hidden'
                          : 'visible',
                    }}
                  >
                    <svg
                      className="arrowUpOutlined"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path d="M12 21c-1.654 0-3-1.346-3-3v-4.764c-1.143 1.024-3.025.979-4.121-.115-1.17-1.169-1.17-3.073 0-4.242l7.121-7.121 7.121 7.121c1.17 1.169 1.17 3.073 0 4.242-1.094 1.095-2.979 1.14-4.121.115v4.764c0 1.654-1.346 3-3 3zm-1-12.586v9.586c0 .551.448 1 1 1s1-.449 1-1v-9.586l3.293 3.293c.379.378 1.035.378 1.414 0 .391-.391.391-1.023 0-1.414l-5.707-5.707-5.707 5.707c-.391.391-.391 1.023 0 1.414.379.378 1.035.378 1.414 0l3.293-3.293z" />
                    </svg>
                  </button>
                </div>
                <button
                  type="submit"
                  disabled={Basket.inProgress}
                  onClick={submitForm}
                  className={`product-button${Basket.inProgress ? ' product-button--loading' : ''}`}
                >
                  {t('product.addToCart')}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      )}
      <div
        className={`product-added-to-basket ${showAddedToCartMessage ? 'show' : 'hide'}`}
        role="status"
        aria-live="polite"
      >
        {t('product.productWasAddedToYourCart')}
        &nbsp;
        <Link
          to={`/${(User.languageType?.code || DEFAULT_LANGUAGE_TYPE.code).toLowerCase()}/basket`}
          className="product-added-to-basket__link"
        >
          {t('navigation.title.basket')}
        </Link>
      </div>
    </div>
  );
}

const mapStateToProps = (state: {
  Product: IProductState;
  ProductWatchdog: IProductWatchdogState;
  Basket: IBasketState;
  User: IUserState;
  Country: ICountryState;
}) => ({
  Product: state.Product,
  ProductWatchdog: state.ProductWatchdog,
  Basket: state.Basket,
  User: state.User,
  Country: state.Country,
});

export default connect(mapStateToProps)(React.memo(Offer));
